import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
  tokens,
  Badge,
  Caption1,
  Body1,
  Title3,
  Title2,
} from '@fluentui/react-components';
import {
  StarFilled,
  SparkleRegular,
  ArrowTrendingRegular,
  BugRegular,
} from '@fluentui/react-icons';

interface UpdateItem {
  type: 'feature' | 'improvement' | 'bugfix';
  title: string;
  description: string;
  date?: string;
  status?: string;
}

interface ChangelogData {
  version: string;
  lastUpdated: string;
  updates: UpdateItem[];
  comingSoon?: UpdateItem[];
}

interface UpdatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  isFirstTime?: boolean;
}

const useStyles = makeStyles({
  modal: {
    maxWidth: '900px',
    width: '90vw',
    maxHeight: '85vh',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  versionBadge: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: 'white',
    fontWeight: tokens.fontWeightSemibold,
  },
  updateList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    paddingRight: '8px',
  },
  updateItem: {
    padding: '20px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow4,
    },
  },
  updateHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  updateTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  updateDate: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  updateDescription: {
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.5',
    margin: 0,
  },
  typeIcon: {
    width: '16px',
    height: '16px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: tokens.colorNeutralForeground3,
  },
  scrollableContent: {
    scrollbarWidth: 'thin',
    scrollbarColor: `${tokens.colorNeutralStroke1} ${tokens.colorNeutralBackground1}`,
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: tokens.colorNeutralBackground1,
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: tokens.colorNeutralStroke1,
      borderRadius: '4px',
      '&:hover': {
        background: tokens.colorNeutralStroke2,
      },
    },
  },
});

// Hardcoded latest updates
const LATEST_UPDATES: UpdateItem[] = [
  {
    type: 'feature',
    title: '🎨 New Theme System',
    description: 'Personalize your experience with four beautiful themes! Choose from Light, Dark, Red, or Blue themes. Access it from your profile menu → Theme. Your preference is automatically saved.',
    date: new Date().toISOString(),
  },
  {
    type: 'improvement',
    title: '📊 Enhanced Org Chart Modal',
    description: 'The organization chart modal is now bigger and better! View all teams at once in a modern grid layout. Each team is displayed as a compact card with easy-to-see member counts and leads. Unassigned members are grouped at the bottom for quick assignment.',
    date: new Date().toISOString(),
  },
  {
    type: 'improvement',
    title: '⚡ Quick Team Member Management',
    description: 'Managing team members is now faster and easier! Click on a team member\'s name to view their profile, or use the X button to quickly remove them from a team. Unassigned members can be assigned to teams directly from the dropdown.',
    date: new Date().toISOString(),
  },
  {
    type: 'improvement',
    title: '🌙 Dark Theme for Volunteer Pool',
    description: 'The Volunteer Pool and Clinic Schedule now feature beautiful dark backgrounds that match your selected theme. All text and elements have been optimized for better visibility and a more cohesive look.',
    date: new Date().toISOString(),
  },
];

const UpdatesModal: React.FC<UpdatesModalProps> = ({ isOpen, onClose, isFirstTime = false }) => {
  const styles = useStyles();

  const handleClose = () => {
    // Mark as seen in localStorage
    localStorage.setItem('hasSeenLatestUpdates', 'true');
    onClose();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'feature':
        return <SparkleRegular className={styles.typeIcon} />;
      case 'improvement':
        return <ArrowTrendingRegular className={styles.typeIcon} />;
      case 'bugfix':
        return <BugRegular className={styles.typeIcon} />;
      default:
        return <SparkleRegular className={styles.typeIcon} />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'feature':
        return tokens.colorPaletteGreenBackground2;
      case 'improvement':
        return tokens.colorPaletteBlueBackground2;
      case 'bugfix':
        return tokens.colorPaletteRedBackground2;
      default:
        return tokens.colorNeutralBackground2;
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'feature':
        return 'New Feature';
      case 'improvement':
        return 'Improvement';
      case 'bugfix':
        return 'Bug Fix';
      default:
        return type;
    }
  };

  // Use hardcoded updates instead of fetching
  const changelog: ChangelogData = {
    version: '2.0.0',
    lastUpdated: new Date().toISOString(),
    updates: LATEST_UPDATES,
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(_, { open }) => !open && handleClose()} modalType={isFirstTime ? "modal" : "non-modal"}>
      <DialogSurface className={styles.modal}>
        <DialogBody style={{ padding: '0', display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
          <DialogTitle>
            <div className={styles.header} style={{ padding: '24px 24px 16px 24px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <StarFilled style={{ color: tokens.colorBrandForeground1, fontSize: '24px' }} />
                <Title2 style={{ margin: 0 }}>What's New</Title2>
                <Badge appearance="filled" className={styles.versionBadge} style={{ fontSize: '12px', padding: '4px 8px' }}>
                  v{changelog.version}
                </Badge>
              </div>
            </div>
          </DialogTitle>
          
          <DialogContent 
            className={styles.scrollableContent}
            style={{ 
              padding: '24px', 
              overflowY: 'auto', 
              flex: 1, 
              minHeight: 0,
            }}
          >
            <Caption1 style={{ color: tokens.colorNeutralForeground3, marginBottom: '24px', display: 'block' }}>
              Welcome back! Here are the latest improvements and features:
            </Caption1>
            
            <div className={styles.updateList}>
              {changelog.updates.map((update, index) => (
                <div key={index} className={styles.updateItem}>
                  <div className={styles.updateHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ color: getTypeColor(update.type), display: 'flex', alignItems: 'center' }}>
                        {getTypeIcon(update.type)}
                      </div>
                      <Title3 className={styles.updateTitle} style={{ fontSize: '18px', margin: 0 }}>
                        {update.title}
                      </Title3>
                      <Badge 
                        appearance="tint" 
                        style={{ 
                          backgroundColor: getTypeColor(update.type),
                          color: 'white',
                          fontWeight: tokens.fontWeightSemibold,
                        }}
                      >
                        {getTypeText(update.type)}
                      </Badge>
                    </div>
                    {update.date && (
                      <Caption1 className={styles.updateDate}>
                        {formatDate(update.date)}
                      </Caption1>
                    )}
                  </div>
                  <Body1 className={styles.updateDescription} style={{ marginTop: '12px', fontSize: '14px', lineHeight: '1.6' }}>
                    {update.description}
                  </Body1>
                </div>
              ))}
            </div>
          </DialogContent>
          
          <DialogActions style={{ padding: '16px 24px', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
            <Button appearance="primary" onClick={handleClose} size="large">
              Got it!
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default UpdatesModal;
